import { useTranslation } from "react-i18next";

import { useRequiredContext } from "@lib/utils";

import {
  MapIndicatorIllustration,
  type MapIndicatorKey,
} from "../../../../organisms/hereAndNow/HaNOccupancyInformation/MapIndicatorIllustration";
import { PanelStackContext } from "../../shared/PanelStack/PanelStackContext";
import { HaNDetailsPage } from "../HaNDetailsPage";
import { InactiveLegendIcon } from "./components/InactiveLegendIcon";
import { OccupancyLegendRow } from "./components/OccupancyLegendRow";
import { OccupancyStatusIcon } from "./components/OccupancyStatusIcon";

const MAP_INDICATORS = Object.keys(MapIndicatorIllustration) as MapIndicatorKey[];

// What the workspace map markers and the sensor and booking statuses mean
export const WorkspaceOccupancyInfoPage = () => {
  const { t } = useTranslation();
  const { pop } = useRequiredContext(PanelStackContext);

  return (
    <HaNDetailsPage
      title={t("workplace:Area")}
      onBack={pop}>
      <div className="flex flex-col gap-10 p-5">
        <h2 className="font-display text-[26px] font-medium leading-8">{t("translation:AvailabilityInformation")}</h2>

        <section className="flex flex-col gap-6">
          <h3 className="text-[17px] font-semibold leading-6">{t("hereAndNow:MapIndicatorInformation")}</h3>

          {MAP_INDICATORS.map(indicator => {
            const Illustration = MapIndicatorIllustration[indicator];

            return (
              <OccupancyLegendRow
                key={indicator}
                icon={<Illustration className="w-10 shrink-0" />}
                title={t(`hereAndNow:MapIndicatorWorkspace${indicator}.Title`)}
                description={t(`hereAndNow:MapIndicatorWorkspace${indicator}.Description`)}
              />
            );
          })}
        </section>

        <section className="flex flex-col gap-6">
          <h3 className="text-[17px] font-semibold leading-6">{t("hereAndNow:SensorData")}</h3>

          <OccupancyLegendRow
            icon={<OccupancyStatusIcon status="quiet" />}
            title={t("translation:ItSeemsQuiet")}
            description={t("translation:ItSeemsQuietDescription")}
          />
          <OccupancyLegendRow
            icon={<OccupancyStatusIcon status="lively" />}
            title={t("translation:ItSeemsLively")}
            description={t("translation:ItSeemsLivelyDescription")}
          />
          <OccupancyLegendRow
            icon={<OccupancyStatusIcon status="busy" />}
            title={t("translation:ItSeemsBusy")}
            description={t("translation:ItSeemsBusyDescription")}
          />
          <OccupancyLegendRow
            icon={<OccupancyStatusIcon status="full" />}
            title={t("translation:ItSeemsFull")}
            description={t("translation:ItSeemsFullDescription")}
          />
          <OccupancyLegendRow
            icon={<OccupancyStatusIcon status="unknown" />}
            title={t("translation:NoSensorData")}
            description={t("translation:NoSensorDataDescription")}
          />
          <OccupancyLegendRow
            icon={<InactiveLegendIcon icon="noBroadcast" />}
            title={t("translation:NoSensorInstalled")}
            description={t("translation:NoSensorInstalledDescriptionWorkspace")}
          />
        </section>

        <section className="flex flex-col gap-6">
          <h3 className="text-[17px] font-semibold leading-6">{t("hereAndNow:BookingData")}</h3>

          <OccupancyLegendRow
            icon={<InactiveLegendIcon icon="noCalendar" />}
            title={t("hereAndNow:AdhocUsageOnly")}
            description={t("hereAndNow:WorkspaceInfoAdHocDetail")}
          />
          <OccupancyLegendRow
            icon={<InactiveLegendIcon icon="calendar" />}
            title={t("hereAndNow:WorkspaceBookings")}
            description={t("hereAndNow:WorkspaceBookingsDescription")}
          />
        </section>
      </div>
    </HaNDetailsPage>
  );
};
